import { Box, Paper, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { useMpkChangeLog } from "../../hooks/useMpkChangeLog";
import { useMpkDimensions } from "../../hooks/useMpkDimensions";
import { DocumentDTO } from "../../types/DocumentDTO";
import StripedDataGrid from "../common/StripedDataGrid";
import { useEffect, useState } from "react";
import { getUsers } from "../../api/users";

import { useAppTranslation } from "../../hooks/useAppTranslation";

type CombinedHistoryRow = {
  id: string;
  date: string;
  action: string | null;
  dimension1: string | null;
  dimension2: string | null;
  amount: string | null;
  costType: string | null;
  status: string | null;
  user: string | null;
  comment: string | null;
};

type Props = {
  document: DocumentDTO;
  companyId: string;
};

export default function DocumentCombinedHistoryTable({
  document,
  companyId,
}: Props) {
  const { rows: mpkRows, loading } = useMpkChangeLog(
    document.id,
    companyId
  );
  const { t } = useAppTranslation("common");

  const mpk = useMpkDimensions(document, companyId);
  const {
    dimensionHeaders,
    dictionaries,
    hasDimension1,
    hasDimension2,
  } = mpk;

  /* ================= USERS MAP ================= */

  const [userMap, setUserMap] = useState<Record<string, string>>({});

  useEffect(() => {
    async function loadUsers() {
      try {
        const users = await getUsers();
        const map: Record<string, string> = {};
        users.forEach((u: any) => {
          map[u.email] = u.display_name ?? u.email;
        });
        setUserMap(map);
      } catch (e) {
        console.error("Nie udało się pobrać użytkowników", e);
      }
    }

    loadUsers();
  }, []);

  if (loading) {
    return <Typography variant="body2">{t("Ładowanie historii…")}</Typography>;
  }

  /* ================= NORMALIZACJA ================= */

  const dim1Values = dictionaries?.dimension1?.values ?? [];
  const dim2Values = dictionaries?.dimension2?.values ?? [];

  const statusRows: CombinedHistoryRow[] =
    document.statusHistory.map((s, idx) => ({
      id: `status-${idx}`,
      date: new Date(s.timestamp).toLocaleString(),
      action: null,
      dimension1: null,
      dimension2: null,
      amount: null,
      costType: null,
      status: s.status,
      user: userMap[s.changedBy] ?? s.changedBy,
      comment: s.comment ?? null,
    }));

  const mpkHistoryRows: CombinedHistoryRow[] =
    mpkRows.map((m) => {
      const dim1 = dim1Values.find(
        (v: any) => v.code === m.dimensionValueCode1
      );
      const dim2 = dim2Values.find(
        (v: any) => v.code === m.dimensionValueCode2
      );

      let resolvedUser: string | null = null;

      if (m.userEMail) {
        resolvedUser =
          userMap[m.userEMail] ??
          m.userEMail;
      } else {
        resolvedUser = t("Nieznany użytkownik");
      }

      return {
        id: `mpk-${m.systemId}`,
        date: new Date(m.systemCreatedAt).toLocaleString(),
        action: m.action,
        dimension1:
          dim1?.name ?? m.dimensionValueCode1 ?? null,
        dimension2:
          dim2?.name ?? m.dimensionValueCode2 ?? null,
        amount: m.amount?.toString() ?? null,
        costType: m.purchaseCostText ?? null,
        status: null,
        user: resolvedUser,
        comment: null,
      };
    });

  const rows: CombinedHistoryRow[] = [
    ...statusRows,
    ...mpkHistoryRows,
  ].sort(
    (a, b) =>
      new Date(b.date).getTime() -
      new Date(a.date).getTime()
  );

  /* ================= RENDER CELLS ================= */

  const StatusTypeCell = ({ value }: { value: string | null }) => {
    const { t } = useAppTranslation("common");
    if (!value) return <>{value}</>;
    return (
      <>
        {t(value, {}, "statuses")}
      </>
    );
  };

  const ActionTypeCell = ({ value }: { value: string | null }) => {
    const { t } = useAppTranslation("common");
    if (!value) return <>{value}</>;
    return (
      <>
        {t(value, {}, "documents")}
      </>
    );
  };

  /* ================= KOLUMNY ================= */

  const columns: GridColDef[] = [
    {
      field: "date",
      headerName: t("history_date"),
      flex: 1,
      minWidth: 160,
      aggregable: false,
    },
    {
      field: "action",
      headerName: t("history_action"),
      flex: 1,
      renderCell: (params) => (
        <ActionTypeCell value={params.value} />
      ),
      aggregable: false,
    },

    ...(hasDimension1
      ? [
          {
            field: "dimension1",
            headerName:
              dimensionHeaders.find(
                (d: any) => d.key === "dimension1"
              )?.label ?? "Wymiar 1",
            flex: 1,
            aggregable: false,
          },
        ]
      : []),

    ...(hasDimension2
      ? [
          {
            field: "dimension2",
            headerName:
              dimensionHeaders.find(
                (d: any) => d.key === "dimension2"
              )?.label ?? "Wymiar 2",
            flex: 1,
            aggregable: false,
          },
        ]
      : []),

    {
      field: "amount",
      headerName: t("history_amount"),
      flex: 1,
      align: "right",
      headerAlign: "right",
      aggregable: false,
    },
    {
      field: "costType",
      headerName: t("history_cost_type"),
      flex: 1,
      aggregable: false,
    },
    {
      field: "status",
      headerName: t("history_status"),
      flex: 1,
      renderCell: (params) => (
        <StatusTypeCell value={params.value} />
      ),
      aggregable: false,
    },
    {
      field: "user",
      headerName: t("history_user"),
      flex: 1,
      aggregable: false,
    },
    {
      field: "comment",
      headerName: t("history_comment"),
      flex: 2,
      aggregable: false,
    },
  ];

  /* ================= RENDER ================= */

  return (
    <Box sx={{ mt: 3, width: "100%" }}>
      <Paper sx={{ p: 2, width: "100%" }}>
        <Typography variant="subtitle1" gutterBottom>
          {t("Historia zmian")}
        </Typography>

        {rows.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            {t("Brak zmian")}
          </Typography>
        ) : (
          <StripedDataGrid
            autoHeight
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            hideFooter
            density="compact"
          />
        )}
      </Paper>
    </Box>
  );
}
